import DOMPurify from 'dompurify';

/**
 * Security utility functions for input validation and sanitization
 * Built Simple - Security Enhancement Module
 */

export class SecurityValidator {
    /**
     * Sanitize HTML content to prevent XSS attacks
     */
    static sanitizeHtml(dirty: string): string {
        if (!dirty || typeof dirty !== 'string') return '';

        return DOMPurify.sanitize(dirty, {
            ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
            ALLOWED_ATTR: ['class'],
            FORBID_ATTR: ['style', 'onload', 'onerror'],
            FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input']
        });
    }

    /**
     * Sanitize plain text input
     */
    static sanitizeText(input: string): string {
        if (!input || typeof input !== 'string') return '';

        return input
            .trim()
            .replace(/[<>]/g, '') // Remove potential HTML tags
            .replace(/javascript:/gi, '') // Remove javascript protocols
            .replace(/on\w+=/gi, ''); // Remove event handlers
    }

    /**
     * Validate email format
     */
    static validateEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Validate password strength
     */
    static validatePassword(password: string): {
        isValid: boolean;
        issues: string[];
    } {
        const issues: string[] = [];

        if (password.length < 8) {
            issues.push('Password must be at least 8 characters long');
        }

        if (!/[A-Z]/.test(password)) {
            issues.push('Password must contain at least one uppercase letter');
        }

        if (!/[a-z]/.test(password)) {
            issues.push('Password must contain at least one lowercase letter');
        }

        if (!/\d/.test(password)) {
            issues.push('Password must contain at least one number');
        }

        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            issues.push('Password must contain at least one special character');
        }

        return {
            isValid: issues.length === 0,
            issues
        };
    }

    /**
     * Generate secure CSRF token
     */
    static generateCSRFToken(): string {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Validate URL to prevent SSRF attacks
     */
    static validateUrl(url: string): boolean {
        try {
            const urlObj = new URL(url);
            const allowedProtocols = ['http:', 'https:'];
            const allowedHosts = ['api.builtsimple.com.au', 'stagingapi.builtsimple.com.au'];

            return allowedProtocols.includes(urlObj.protocol) && allowedHosts.includes(urlObj.hostname);
        } catch {
            return false;
        }
    }

    /**
     * Rate limiting helper
     */
    static checkRateLimit(key: string, maxAttempts: number = 5, windowMs: number = 300000): boolean {
        const now = Date.now();
        const windowStart = now - windowMs;

        const attempts = JSON.parse(localStorage.getItem(`rate_limit_${key}`) || '[]').filter(
            (timestamp: number) => timestamp > windowStart
        );

        if (attempts.length >= maxAttempts) {
            return false; // Rate limit exceeded
        }

        attempts.push(now);
        localStorage.setItem(`rate_limit_${key}`, JSON.stringify(attempts));
        return true;
    }

    /**
     * Log security events for monitoring
     */
    static logSecurityEvent(event: string, details: any = {}): void {
        const securityLog = {
            timestamp: new Date().toISOString(),
            event,
            details,
            userAgent: navigator.userAgent,
            url: window.location.href
        };

        // Store locally for now (in production, send to security monitoring service)
        const existingLogs = JSON.parse(localStorage.getItem('security_logs') || '[]');
        existingLogs.push(securityLog);

        // Keep only last 100 logs to prevent storage bloat
        if (existingLogs.length > 100) {
            existingLogs.splice(0, existingLogs.length - 100);
        }

        localStorage.setItem('security_logs', JSON.stringify(existingLogs));

        // Log to console in development
        if (import.meta.env.DEV) {
            console.warn('Security Event:', securityLog);
        }
    }
}

// Global security helper functions
export const sanitizeHtml = SecurityValidator.sanitizeHtml;
export const sanitizeText = SecurityValidator.sanitizeText;
export const validateEmail = SecurityValidator.validateEmail;
export const validatePassword = SecurityValidator.validatePassword;
export const logSecurityEvent = SecurityValidator.logSecurityEvent;
